/**
 * EventsByOrganization — R5 (2026-05-17).
 *
 * Events list scoped to the operator's filter set, with attendance + verified
 * member counts hydrated from the bulk close-rate RPC, and sales hierarchy
 * names resolved via the bulk position-name resolver. Exportable to XLSX
 * with the same hydrated columns.
 *
 * This is the canonical "use a report to answer 'what happened where'"
 * surface. Uses get_events_for_organizations RPC which avoids the R4
 * unbounded prefetch (post-ship audit finding #2).
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getEventsForOrganizations,
  getEventCloseRateStatsBulk,
  resolvePositionNamesBulk,
  indexCloseRateStatsByEventId,
  indexPositionsById,
  type EventReportRow,
  type ReportEventsFilter,
} from "../../services/reportsSuite.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";
import EventStatusBadge from "../../components/events/EventStatusBadge";

const PAGE_SIZE = 50;

const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  try {
    const d = new Date(iso);
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
};

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
): ReportEventsFilter => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  status: f.status,
  type: f.type,
  state: f.state,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  page,
  limit,
});

const EventsByOrganization: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever any filter changes
  React.useEffect(() => {
    setPage(1);
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.state,
    filters.type,
    filters.status,
    filters.orgId,
  ]);

  const queryKey = ["report-events-by-org", filters, page];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      getEventsForOrganizations(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Hydrate close-rate stats + position names for the visible page
  const eventIds = useMemo(() => rows.map((r: any) => r.id), [rows]);
  const positionIds = useMemo(() => {
    const ids: string[] = [];
    for (const r of rows) {
      for (const k of [
        r.sales_position_id,
        r.territory_manager_position_id,
        r.regional_manager_position_id,
        r.sales_director_position_id,
      ]) {
        if (k) {
          ids.push(k);
        }
      }
    }
    return Array.from(new Set(ids));
  }, [rows]);

  const { data: statsRows } = useQuery({
    queryKey: ["events-bulk-close-rate", eventIds],
    queryFn: () => getEventCloseRateStatsBulk(eventIds),
    enabled: eventIds.length > 0,
  });
  const { data: positionRows } = useQuery({
    queryKey: ["positions-bulk", positionIds],
    queryFn: () => resolvePositionNamesBulk(positionIds),
    enabled: positionIds.length > 0,
  });

  const statsByEvent = useMemo(
    () => indexCloseRateStatsByEventId(statsRows ?? []),
    [statsRows],
  );
  const positionsById = useMemo(
    () => indexPositionsById(positionRows ?? []),
    [positionRows],
  );

  const displayName = (positionId: string | null) => {
    if (!positionId) {
      return "";
    }
    const p = positionsById.get(positionId);
    if (!p) {
      return positionId;
    }
    const label = p.assigned_name || p.title;
    return p.is_active ? label : `${label} (inactive)`;
  };

  const EXPORT_COLUMNS = [
    "Event ID",
    "Event Name",
    "Event Date",
    "End Date",
    "Type",
    "Status",
    "State",
    "Location Name",
    "Location City",
    "Attendance",
    "Verified Members",
    "Close Rate %",
    "Lead/Event Rep",
    "Territory Manager",
    "Regional Manager",
    "Sales Director",
    "Voucher Count",
    "Cancellation Reason",
    "Cancellation Notes",
    "Created At",
  ];

  // Post-ship audit must-fix #2: bulk-hydrate stats + position names
  // against pageRows (ALL exported rows, not just visible page). ExportButton
  // calls mapRows once with the full concatenated result; the visible-page
  // caches above are only good for the table render, not for export. mapRows
  // chunks pageRows by the 5000-cap on each bulk RPC.
  const chunk = <T,>(arr: T[], size: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }
    return out;
  };

  const mapRows = async (
    pageRows: EventReportRow[],
  ): Promise<Record<string, unknown>[]> => {
    const eventIds = pageRows.map((r) => r.id).filter(Boolean);
    const positionIds = Array.from(
      new Set(
        pageRows.flatMap((r) =>
          [
            r.sales_position_id,
            r.territory_manager_position_id,
            r.regional_manager_position_id,
            r.sales_director_position_id,
          ].filter((id): id is string => Boolean(id)),
        ),
      ),
    );

    const [statsChunks, positionChunks] = await Promise.all([
      Promise.all(
        chunk(eventIds, 5000).map((c) => getEventCloseRateStatsBulk(c)),
      ),
      Promise.all(
        chunk(positionIds, 5000).map((c) => resolvePositionNamesBulk(c)),
      ),
    ]);
    const exportStatsByEvent = indexCloseRateStatsByEventId(statsChunks.flat());
    const exportPositionsById = indexPositionsById(positionChunks.flat());

    const exportDisplayName = (positionId: string | null) => {
      if (!positionId) {
        return "";
      }
      const p = exportPositionsById.get(positionId);
      if (!p) {
        return positionId;
      }
      const label = p.assigned_name || p.title;
      return p.is_active ? label : `${label} (inactive)`;
    };

    return pageRows.map((r) => {
      const s = exportStatsByEvent.get(r.id);
      return {
        "Event ID": r.id,
        "Event Name": r.title,
        "Event Date": formatDate(r.start_time),
        "End Date": formatDate(r.end_time),
        Type: r.type,
        Status: r.status,
        State: r.location_state || r.state || "",
        "Location Name": r.location_name || "",
        "Location City": r.location_city || "",
        Attendance: s?.attendance_count ?? r.attendance_count ?? "",
        "Verified Members": s?.verified_member_count ?? "",
        "Close Rate %":
          s?.close_rate !== null && s?.close_rate !== undefined
            ? (s.close_rate * 100).toFixed(2)
            : "",
        "Lead/Event Rep": exportDisplayName(r.sales_position_id),
        "Territory Manager": exportDisplayName(r.territory_manager_position_id),
        "Regional Manager": exportDisplayName(r.regional_manager_position_id),
        "Sales Director": exportDisplayName(r.sales_director_position_id),
        "Voucher Count": r.cta_count ?? "",
        "Cancellation Reason": r.cancellation_reason ?? "",
        "Cancellation Notes": r.cancellation_notes ?? "",
        "Created At": formatDate(r.created_at),
      };
    });
  };

  return (
    <div className="space-y-4">
      <ReportPageHeader
        title="Events by Organization"
        description="Filter events by org, date, state, or type. Export to Excel with attendance, verified-member count, and close rate hydrated."
        actions={
          <ExportButton
            filters={filters}
            fetchPage={(f, p, l) =>
              getEventsForOrganizations(filterToRpc(f, p, l)).then((res) => ({
                rows: res.rows,
                total: res.total,
              }))
            }
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="events-by-organization"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showStatus={true}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Lead Rep</th>
              <th className="px-3 py-2 text-right">Attendance</th>
              <th className="px-3 py-2 text-right">Verified</th>
              <th className="px-3 py-2 text-right">Close %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  No matching events.
                </td>
              </tr>
            )}
            {rows.map((r: any) => {
              const s = statsByEvent.get(r.id);
              return (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <Link
                      to={`/events/${r.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {r.title}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{formatDate(r.start_time)}</td>
                  <td className="px-3 py-2">{r.type}</td>
                  <td className="px-3 py-2">
                    {/* Shared badge — same derived liveness as the events list. */}
                    <EventStatusBadge
                      event={{
                        status: r.status,
                        start_time: r.start_time,
                        end_time: r.end_time,
                      }}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {r.location_state || r.state || ""}
                  </td>
                  <td className="px-3 py-2">
                    {displayName(r.sales_position_id)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.attendance_count ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {s?.verified_member_count ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {s?.close_rate !== null && s?.close_rate !== undefined
                      ? `${(s.close_rate * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventsByOrganization;
